<template>
    <Layout>
        <template v-slot:title>
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb">
                    <li class="breadcrumb-item">
                        <router-link tag="a" :to="{ name: 'log.diagnostics' }">Live Diagnostics</router-link>
                    </li>
                    <li class="breadcrumb-item active">Live Diagnostic Detail</li>
                </ol>
            </nav>
        </template>

        <template v-slot:content>
            <Loader v-if="isLoading"></Loader>

            <div v-else-if="survey" class="row">
                <div class="col-md-8">
                    <div class="card">
                        <div class="card-header">
                            <h3 class="card-title">Live Diagnostic Information</h3>
                        </div>
                        <div class="card-body">
                            <table class="table table-bordered">
                                <tbody>
                                    <tr>
                                        <th style="width: 200px">Submitted At</th>
                                        <td>
                                            <div class="d-flex align-items-center">
                                                <ClockIcon size="1.1x" class="mr-2"></ClockIcon>
                                                <span>{{ survey.created_at_readable }}</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>User</th>
                                        <td>
                                            <div v-if="survey.user && survey.user._id">
                                                <router-link
                                                    :to="{ name: 'user.edit', params: { id: survey.user._id } }"
                                                    style="width: fit-content"
                                                >
                                                    {{ survey.user.name }}
                                                </router-link>
                                                <span>({{ survey.user._id }})</span>
                                            </div>
                                            <span v-else class="text-muted">Unknown User</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Quiz</th>
                                        <td>
                                            <div v-if="survey.quiz && survey.quiz._id">
                                                <router-link
                                                    :to="{
                                                        name: 'quizzes.quiz.questions',
                                                        params: { id: survey.quiz._id }
                                                    }"
                                                >
                                                    {{ survey.quiz.title }}
                                                </router-link>
                                                <span>({{ survey.quiz._id }})</span>
                                            </div>
                                            <span v-else class="text-muted">Unknown Quiz</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Problems</th>
                                        <td>
                                            <span v-if="diagnoseData.problems && diagnoseData.problems.length > 0">
                                                {{ diagnoseData.problems.join(', ') }}
                                            </span>
                                            <span v-else class="text-muted font-italic">No problems reported</span>
                                        </td>
                                    </tr>
                                    <tr v-if="diagnoseData.device">
                                        <th>Device</th>
                                        <td>
                                            <div v-if="diagnoseData.device.userAgent">
                                                <strong>User Agent:</strong>
                                                <div class="text-muted small">{{ diagnoseData.device.userAgent }}</div>
                                            </div>
                                            <div v-if="diagnoseData.device.networkConnection" class="mt-2">
                                                <strong>Network Connection:</strong>
                                                {{ diagnoseData.device.networkConnection }}
                                            </div>
                                            <div v-if="diagnoseData.device.dimension" class="mt-2">
                                                <strong>Dimension:</strong> {{ diagnoseData.device.dimension }}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="diagnoseData.numberPlayer">
                                        <th>Number of Players</th>
                                        <td>
                                            <div v-if="diagnoseData.numberPlayer.start">
                                                <strong>Start:</strong> {{ diagnoseData.numberPlayer.start }}
                                            </div>
                                            <div v-if="diagnoseData.numberPlayer.end" class="mt-1">
                                                <strong>End:</strong> {{ diagnoseData.numberPlayer.end }}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="diagnoseData.playMode">
                                        <th>Play Mode</th>
                                        <td>
                                            <span
                                                class="badge"
                                                :class="
                                                    diagnoseData.playMode === 'single' ? 'badge-primary' : 'badge-info'
                                                "
                                            >
                                                {{ diagnoseData.playMode === 'single' ? 'Single' : 'Team' }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="diagnoseData.masterType">
                                        <th>Master Type</th>
                                        <td>
                                            <span
                                                class="badge"
                                                :class="
                                                    diagnoseData.masterType === 'player'
                                                        ? 'badge-success'
                                                        : 'badge-warning'
                                                "
                                            >
                                                {{
                                                    diagnoseData.masterType === 'player'
                                                        ? 'Player (Join as player)'
                                                        : 'Master (Join as quiz master)'
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="diagnoseData.liveSettings">
                                        <th>Live Settings</th>
                                        <td>
                                            <pre class="bg-light p-2 rounded mb-0">{{
                                                formatLiveSettings(diagnoseData.liveSettings)
                                            }}</pre>
                                        </td>
                                    </tr>
                                    <tr v-if="diagnoseData.error">
                                        <th>Error</th>
                                        <td>
                                            <pre class="bg-light p-2 rounded mb-0">{{ diagnoseData.error }}</pre>
                                        </td>
                                    </tr>
                                    <tr v-if="diagnoseData.playerResult">
                                        <th>Player Result</th>
                                        <td>
                                            <textarea
                                                :value="formatPlayerResult(diagnoseData.playerResult)"
                                                class="form-control"
                                                rows="30"
                                                readonly
                                            ></textarea>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="col-md-4">
                    <!-- Actions Card -->
                    <div class="card">
                        <div class="card-header">
                            <h3 class="card-title">Quick Actions</h3>
                        </div>
                        <div class="card-body">
                            <button
                                @click.stop="deleteDiagnostic"
                                class="btn btn-danger btn-block d-flex align-items-center justify-content-center"
                            >
                                <TrashIcon size="1x" class="mr-2"></TrashIcon>
                                <span>Delete Live Diagnostic</span>
                            </button>
                            <router-link
                                v-if="survey.quiz && survey.quiz._id"
                                :to="{ name: 'quizzes.quiz.questions', params: { id: survey.quiz._id } }"
                                class="btn btn-info btn-block mt-2 d-flex align-items-center justify-content-center"
                            >
                                <CheckCircleIcon size="1x" class="mr-2"></CheckCircleIcon>
                                <span>View Related Quiz</span>
                            </router-link>
                            <router-link
                                v-if="survey.user && survey.user._id"
                                :to="{ name: 'user.edit', params: { id: survey.user._id } }"
                                class="btn btn-primary btn-block mt-2 d-flex align-items-center justify-content-center"
                            >
                                <UserIcon size="1x" class="mr-2"></UserIcon>
                                <span>View User Profile</span>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="alert alert-warning">
                <h4>Live Diagnostic Not Found</h4>
                <p>The live diagnostic you're looking for doesn't exist or has been deleted.</p>
                <router-link :to="{ name: 'log.diagnostics' }" class="btn btn-primary">
                    Back to Live Diagnostic List
                </router-link>
            </div>
        </template>
    </Layout>
</template>

<script>
import Layout from '@/components/Layout'
import Loader from '@/components/Loader'
import { ClockIcon, UserIcon, CheckCircleIcon, TrashIcon } from 'vue-feather-icons'

export default {
    name: 'diagnostic-detail',

    components: {
        Layout,
        Loader,
        ClockIcon,
        UserIcon,
        CheckCircleIcon,
        TrashIcon
    },

    data() {
        return {
            isLoading: false,
            survey: null,
            diagnoseData: null
        }
    },

    mounted() {
        window.scrollTo(0, 0)
        this.fetchSurvey()
    },

    methods: {
        async fetchSurvey() {
            const surveyId = this.$route.params.id

            if (!surveyId) {
                this.$router.push({ name: 'log.diagnostics' })
                return
            }

            this.isLoading = true

            try {
                const response = await this.$store.dispatch('diagnostic/findDiagnosticById', surveyId)

                if (response.status === 200 && response.data.data) {
                    this.survey = response.data.data
                    this.diagnoseData = response.data.data.diagnose_data
                } else {
                    window.shout.error('Live Diagnostic not found')
                }
            } catch (error) {
                console.error('Error fetching live diagnostic:', error)
                window.shout.error('Failed to load live diagnostic details')
            } finally {
                this.isLoading = false
            }
        },

        deleteDiagnostic() {
            this.$vueConfirm.confirm(
                {
                    message: 'Are you sure you want to delete this live diagnostic?',
                    button: {
                        no: 'Cancel',
                        yes: 'Delete'
                    }
                },
                confirm => {
                    if (confirm) {
                        this.confirmDelete()
                    }
                }
            )
        },

        async confirmDelete() {
            try {
                const response = await this.$store.dispatch('diagnostic/deleteDiagnostic', this.$route.params.id)

                if (response.status === 200) {
                    window.shout.success('Live Diagnostic deleted successfully')
                    this.$router.push({ name: 'log.diagnostics' })
                } else {
                    window.shout.error('Failed to delete live diagnostic')
                }
            } catch (error) {
                console.error('Error deleting live diagnostic:', error)
                window.shout.error('Failed to delete live diagnostic')
            }
        },

        formatPlayerResult(playerResult) {
            if (typeof playerResult === 'string') {
                return playerResult
            }
            if (typeof playerResult === 'object') {
                return JSON.stringify(playerResult, null, 2)
            }
            return String(playerResult)
        },

        formatLiveSettings(liveSettings) {
            if (typeof liveSettings === 'string') {
                try {
                    // Try to parse if it's a JSON string
                    const parsed = JSON.parse(liveSettings)
                    return JSON.stringify(parsed, null, 2)
                } catch {
                    return liveSettings
                }
            }
            if (typeof liveSettings === 'object') {
                return JSON.stringify(liveSettings, null, 2)
            }
            return String(liveSettings)
        }
    }
}
</script>

<style lang="scss" scoped>
.feedback-text {
    white-space: pre-wrap;
    word-wrap: break-word;
    line-height: 1.6;
    font-size: 1rem;
}

code {
    background-color: #f4f4f4;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 0.9em;
}

.bg-gradient-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.table th {
    background-color: #f8f9fa;
}

.theme-dark-mode {
    .table th {
        background-color: rgba(255, 255, 255, 0.05);
    }

    code {
        background-color: rgba(255, 255, 255, 0.1);
        color: #e83e8c;
    }
}
</style>
